import * as vp from 'vopay';
import * as Yup from 'yup';
const { TextField, Select } = vp;
import { FieldData } from '../../../../Form/types';
import { resolveSpendableBalance } from '../../../resolveSpendableBalance';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';

const SEARCH_MIN_CHARS = 3;
const SEARCH_DEBOUNCE_MS = 300;

type AccountTranferRowsProp = {
  MainAccountName: string;
  MainAccountId: string;
  MainAccountBalance: string;
  MainClientAccountsEnabled: boolean;
  fromSubAccounts: vp.PortalSubAccount[];
  toSubAccounts: vp.PortalSubAccount[];
  fromFetching: boolean;
  toFetching: boolean;
  country: string;
  initialFromLabel: string;
  initialFromAccountId: string;
  initialCurrency: string;
  setFieldValueRef: React.RefObject<
    ((field: string, value: string | undefined, shouldValidate?: boolean) => void) | null
  >;
  fromTokenRef: React.RefObject<string | undefined>;
  onFromResolveError: () => void;
  onSearchFromSubAccounts: (searchText: string) => void;
  onSearchToSubAccounts: (searchText: string) => void;
  fetchFromSubAccountsAsync: (params: vp.SubAccountsRequest) => Promise<vp.SubAccountsResponse>;
  fetchToSubAccountsAsync: (params: vp.SubAccountsRequest) => Promise<vp.SubAccountsResponse>;
};

export type AccountTransferRequest = {
  Amount: string;
  RecipientAccountID: string;
  DebitorAccountID: string;
  Currency: string;
  FromBalance?: string;
};
export type AccountTransferSerializedRequest = {
  Amount: number;
  RecipientAccountID: string;
  DebitorAccountID: string;
  Currency: string;
};

export const useAccountTransferRows = ({
  MainAccountName,
  MainAccountBalance,
  MainAccountId,
  MainClientAccountsEnabled,
  fromSubAccounts,
  toSubAccounts,
  fromFetching,
  toFetching,
  country,
  initialFromLabel,
  initialFromAccountId,
  initialCurrency,
  setFieldValueRef,
  fromTokenRef,
  onFromResolveError,
  onSearchFromSubAccounts,
  onSearchToSubAccounts,
  fetchFromSubAccountsAsync,
  fetchToSubAccountsAsync,
}: AccountTranferRowsProp): { fieldDataList: FieldData<AccountTransferRequest>[]; isFromResolved: boolean } => {
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.List.OrgChart.AccountTransfer' });
  const [isFromResolved, setIsFromResolved] = useState(false);
  const [fromBankLabel, setFromBankLabel] = useState(initialFromLabel);
  const [toBankLabel, setToBankLabel] = useState('');
  const [fromSearchText, setFromSearchText] = useState('');
  const [toSearchText, setToSearchText] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState(initialCurrency);
  const [selectedDebitorAccountID, setSelectedDebitorAccountID] = useState(initialFromAccountId);
  const [selectedRecipientAccountID, setSelectedRecipientAccountID] = useState('');

  const fromResolveRequestId = useRef(0);
  const toResolveRequestId = useRef(0);

  const CurrencyOptions = {
    ...vp.CURRENCY_LIST_OPTIONS,
  };

  async function resolveAccount(
    accountId: string,
    currencySelected: string,
    fetchAccountDetail: (params: vp.SubAccountsRequest) => Promise<vp.SubAccountsResponse>,
  ): Promise<{ label: string; balance?: string; token?: string }> {
    if (accountId === MainAccountId) {
      const balance = await resolveSpendableBalance({
        clientAccountsEnabled: MainClientAccountsEnabled,
        currency: currencySelected,
        fallbackBalance: MainAccountBalance || '0.00',
      });

      return { label: `${MainAccountName} ($${vp.formattedCurrency(balance)})`, balance };
    }

    const res = await fetchAccountDetail({ ActiveOnly: true, ShowToken: true, Limit: 1, SubaccountID: accountId });
    const found = res.Subaccounts.find((account) => account.AccountID === accountId);

    if (!found) throw new Error(`Account ${accountId} not found`);
    // The type says Token is always set, but it only comes back when ShowToken
    // is honoured — without it the balance fetch below would run as the parent.
    if (!found.Token) throw new Error(`No token returned for sub-account ${accountId}`);

    const aggregateBalance =
      Object.values(found.Balances || {}).find((bal) => bal.Currency === currencySelected)?.AvailableBalance || '0.00';

    const balance = await resolveSpendableBalance({
      clientAccountsEnabled: found.ClientAccountsEnabled,
      currency: currencySelected,
      fallbackBalance: aggregateBalance,
      bearer: found.Token,
    });

    return { label: `${found.AccountName} ($${vp.formattedCurrency(balance)})`, balance, token: found.Token };
  }

  useEffect(() => {
    if (!selectedDebitorAccountID) {
      setFromBankLabel('');
      fromTokenRef.current = undefined;
      setIsFromResolved(false);
      setFieldValueRef.current?.('FromBalance', undefined, false);

      return;
    }

    const requestId = ++fromResolveRequestId.current;
    fromTokenRef.current = undefined;
    setIsFromResolved(false);

    resolveAccount(selectedDebitorAccountID, selectedCurrency, fetchFromSubAccountsAsync)
      .then(({ label, balance, token }) => {
        if (requestId !== fromResolveRequestId.current) return;
        setFromBankLabel(label);
        fromTokenRef.current = token;
        setIsFromResolved(true);
        setFieldValueRef.current?.('FromBalance', balance, false);
      })
      .catch((error) => {
        if (requestId !== fromResolveRequestId.current) return;
        console.error(error);
        setFromBankLabel('');
        fromTokenRef.current = undefined;
        setIsFromResolved(false);
        setFieldValueRef.current?.('FromBalance', undefined, false);
        onFromResolveError();
      });
  }, [
    selectedDebitorAccountID,
    selectedCurrency,
    MainAccountBalance,
    MainAccountId,
    MainAccountName,
    MainClientAccountsEnabled,
  ]);

  // Same for the "To" side — display only, it carries no validation ceiling.
  useEffect(() => {
    if (!selectedRecipientAccountID) {
      setToBankLabel('');

      return;
    }

    const requestId = ++toResolveRequestId.current;

    resolveAccount(selectedRecipientAccountID, selectedCurrency, fetchToSubAccountsAsync)
      .then(({ label }) => {
        if (requestId !== toResolveRequestId.current) return;
        setToBankLabel(label);
      })
      .catch((error) => {
        if (requestId !== toResolveRequestId.current) return;
        console.error(error);
        setToBankLabel('');
      });
  }, [
    selectedRecipientAccountID,
    selectedCurrency,
    MainAccountBalance,
    MainAccountId,
    MainAccountName,
    MainClientAccountsEnabled,
  ]);

  const fieldDataList: FieldData<AccountTransferRequest>[] = [
    {
      label: () => t('Currency.label'),
      fieldName: 'Currency',
      getInputState: () => 'required',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <Select
            version="2"
            options={country === 'US' ? vp.convertToOptions(vp.US_CURRENCY_ONLY) : vp.convertToOptions(CurrencyOptions)}
            placeholder={t('Currency.placeholder')}
            {...fieldData}
            onChange={(e, { value }) => {
              fieldData.onChange(e);
              setSelectedCurrency(value);
            }}
          />
        );
      },
      showField: () => true,
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('From.label'),
      fieldName: 'DebitorAccountID',
      getInputState: () => 'required',
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({});

        const FromBankAccountsOptions = buildSubAccountOptions(
          fromSubAccounts.filter((data) => ctx.values.RecipientAccountID !== data.APIAccountID),
          false,
          fromSearchText,
        );

        return (
          <Select
            version="2"
            options={FromBankAccountsOptions}
            placeholder={t('From.placeholder')}
            sort={false}
            filterable
            loading={fromFetching}
            emptyFilterMsg={t('From.noResults', { defaultValue: 'No sub-accounts found' })}
            onFilter={(e) => {
              e.preventDefault();
              setFromSearchText(e.filter);
              if (e.filter.length > 0 && e.filter.length < SEARCH_MIN_CHARS) return;
              e.debounce(() => onSearchFromSubAccounts(e.filter), SEARCH_DEBOUNCE_MS);
            }}
            renderSelectedValue={(_, { value, Placeholder }) => {
              if (!value) return <Placeholder />;

              return <>{fromBankLabel || value}</>;
            }}
            {...fieldData}
            onChange={(e, { value }) => {
              fieldData.onChange(e);
              setSelectedDebitorAccountID(value);
            }}
          />
        );
      },
      showField: () => true,
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('To.label'),
      fieldName: 'RecipientAccountID',
      getInputState: () => 'required',
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({});

        const ToBankAccountsOptions = buildSubAccountOptions(
          toSubAccounts.filter((data) => ctx.values.DebitorAccountID !== data.APIAccountID),
          ctx.values.DebitorAccountID === MainAccountId,
          toSearchText,
        );

        return (
          <Select
            version="2"
            options={ToBankAccountsOptions}
            placeholder={t('To.placeholder')}
            sort={false}
            filterable
            loading={toFetching}
            emptyFilterMsg={t('To.noResults', { defaultValue: 'No sub-accounts found' })}
            onFilter={(e) => {
              e.preventDefault();
              setToSearchText(e.filter);
              if (e.filter.length > 0 && e.filter.length < SEARCH_MIN_CHARS) return;
              e.debounce(() => onSearchToSubAccounts(e.filter), SEARCH_DEBOUNCE_MS);
            }}
            renderSelectedValue={(_, { value, Placeholder }) => {
              if (!value) return <Placeholder />;

              return <>{toBankLabel || value}</>;
            }}
            {...fieldData}
            onChange={(e, { value }) => {
              fieldData.onChange(e);
              setSelectedRecipientAccountID(value);
            }}
          />
        );
      },
      showField: () => true,
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('Amount.label'),
      fieldName: 'Amount',
      getInputState: () => 'required',
      serializeField: (value) => Number(vp.sanitizeToNumeric(value.Amount)),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField preset="currency" {...fieldData} variant="outlineShadow" />;
      },
      validate: (ctx) => {
        const availableBalance = ctx.values.FromBalance;

        return Yup.string()
          .test('amount-validation', t('Validation.minimumAmount'), (value) => {
            const amountValidation = Number(vp.sanitizeToNumeric(value)) <= 0.0;

            return !amountValidation;
          })
          .test(
            'amount-validation',
            t('Validation.insufficientFunds', {
              maxAmount: vp.formattedCurrency(String(availableBalance ?? '0.00')),
            }),
            (value) => {
              if (availableBalance === undefined) return true;
              const max = Number(vp.sanitizeToNumeric(availableBalance));
              if (Number.isNaN(max)) return true;

              return Number(vp.sanitizeToNumeric(value)) <= max;
            },
          )
          .required();
      },
      showField: () => true,
    },
  ];

  return { fieldDataList, isFromResolved };

  function buildSubAccountOptions(
    lists: vp.PortalSubAccount[],
    removeMain: boolean,
    searchText: string,
  ): { label: string; value: string }[] {
    const isSearching = searchText.length >= SEARCH_MIN_CHARS;
    const mainMatches = MainAccountName.toLowerCase().includes(searchText.toLowerCase());

    const initialOption =
      removeMain || (isSearching && !mainMatches) ? [] : [{ label: MainAccountName, value: MainAccountId }];

    return [
      ...initialOption,
      ...lists.map((data) => ({
        value: data.APIAccountID,
        label: data.AccountName,
      })),
    ];
  }
};
